import { Users } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { accentPaletteColorForSeed } from "~/lib/accentPalette";
import { cn } from "~/lib/utils";
import { getAvatarInitials } from "~/utils/strings";

interface PersonAvatarProps {
  /** Display name, or the email when the directory has no name for them. */
  name: string;
  /** Seeds the fallback color, so one person is one color across surfaces. */
  email?: string;
  imageUrl?: string | null;
  size?: number;
  className?: string;
}

/**
 * A person's photo when they have one, otherwise their initials on a color
 * derived from their email. The access controller's `image_url` 404s for
 * anyone without a photo, so the fallback is the common case.
 */
export const PersonAvatar = ({
  name,
  email,
  imageUrl,
  size = 24,
  className,
}: PersonAvatarProps) => {
  const color = accentPaletteColorForSeed(email ?? name);
  return (
    <Avatar className={cn("shrink-0", className)} style={{ width: size, height: size }}>
      <AvatarImage src={imageUrl ?? undefined} alt={name} />
      <AvatarFallback
        className="font-medium"
        style={{
          backgroundColor: color.bg,
          color: color.fg ?? color.accent,
          // Floored so two initials stay readable in the 20px table-row avatars.
          fontSize: Math.max(9, Math.round(size * 0.4)),
        }}
      >
        {getAvatarInitials(name)}
      </AvatarFallback>
    </Avatar>
  );
};

/** The `PersonAvatar` counterpart for a group principal. */
export const GroupAvatar = ({ size = 24, className }: { size?: number; className?: string }) => (
  <span
    className={cn(
      "flex shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground",
      className
    )}
    style={{ width: size, height: size }}
  >
    <Users style={{ width: Math.round(size * 0.58), height: Math.round(size * 0.58) }} />
  </span>
);
